/* Sitenin bütün sayfaları — saf, fs'siz.

   Üretici (generate.ts) şablonları diskten okuyup buraya veriyor; panelin
   önizlemesi (apps/admin/src/editor/page.ts) aynı şablonları paketinden
   veriyor. İkisi aynı işlevi çağırdığı için önizleme yayındaki sayfanın
   aynısı — ana sayfa da, works, blog, yazılar ve 404 de.

   Her sayfanın menüsü ve footer'ı tasarım belgesinin ortak alanlarından
   (`shared`), works/blog/404'ün başlık bölgeleri `pages`'ten basılıyor;
   eksik parçalar bugünkü sitenin karşılığı (blocks/defaults.ts). */

import { type Block, type Design } from '@frawgraphy/shared';
import type { SiteContent } from './content.ts';
import { render, type Partials } from './template.ts';
import type { UrlMode } from './urls.ts';
import { renderBlocks, type RenderCtx } from './blocks/render.ts';
import { renderComponent, renderMobileMenu } from './blocks/components.ts';
import { photoResolver } from './blocks/photos.ts';
import { resolveDesign, type PageName } from './blocks/defaults.ts';
import {
  albumView,
  coverflowView,
  homeChrome,
  innerChrome,
  notFoundChrome,
  postCardsView,
  postChrome,
  postPageView,
  servicesView,
  worksView,
  type Lang
} from './view.ts';

export type SiteTemplates = {
  /** index, works, album, blog, post, 404 */
  pages: Record<string, string>;
  partials: Partials;
  /** templates/blocks/<tip>.html */
  blocks: Record<string, string>;
};

export type PageOptions = {
  lang: Lang;
  mode: UrlMode;
  turnstile: { siteKey: string } | null;
  /** Önizleme: blok ve alan işaretleri basılır. */
  edit: boolean;
};

export type RenderedPage = { path: string; body: string };

/** Ana sayfada görünen bölüm çapaları — menü ölü bağlantı basmasın. */
function visibleAnchors(home: Block[]): Set<string> {
  const out = new Set<string>(['hero']);
  for (const b of home) if (b.type === 'section' && !b.style?.hidden) out.add(b.anchor);
  return out;
}

/** Bütün sayfaları ve topladıkları EN sözlüğünü döndürür. */
export function renderPages(
  content: SiteContent,
  design: Design,
  t: SiteTemplates,
  opts: PageOptions
): { pages: RenderedPage[]; en: Record<string, string>; content: SiteContent } {
  const site = createSite(content, design, t, opts);
  const pages: RenderedPage[] = [
    { path: 'index.html', body: site.page('index') },
    { path: 'works.html', body: site.page('works') },
    ...content.albums.map((album) => ({ path: `album-${album.slug}.html`, body: site.page('album', album.slug) })),
    { path: 'blog.html', body: site.page('blog') },
    ...content.posts.map((post) => ({ path: `blog/${post.slug}.html`, body: site.page('post', post.slug) })),
    { path: '404.html', body: site.page('404') }
  ];
  /* Gövdesi bloklardan türetilmiş içerik: llms.txt ve kelime sayımı da
     yayındaki gövdeyi görsün. */
  return { pages, en: site.en, content: site.content };
}

export type PageKey = 'index' | 'works' | 'album' | 'blog' | 'post' | '404';

/**
 * Tek tek sayfa çizen nesne. `page('post', slug)` gibi çağrılıyor; panel
 * yalnızca o an düzenlenen sayfayı çiziyor, üretici hepsini.
 */
export function createSite(source: SiteContent, design: Design, t: SiteTemplates, opts: PageOptions) {
  const doc = resolveDesign(design, source);
  const en: Record<string, string> = {};
  const ctx = { lang: opts.lang, mode: opts.mode };
  const photo = photoResolver(source, ctx);
  const anchors = visibleAnchors(doc.home);
  const nav = doc.shared.find((b): b is Extract<Block, { type: 'nav' }> => b.type === 'nav');
  const footer = doc.shared.find((b): b is Extract<Block, { type: 'footer' }> => b.type === 'footer');

  const renderCtx = (pageScope: Record<string, unknown>): RenderCtx => ({
    edit: opts.edit,
    en,
    photo,
    templates: t.blocks,
    partials: t.partials,
    pageScope,
    anchors
  });

  /* Blokları olan yazının gövdesi bloklardan, yazı kipinde (eski gövdelerin
     işaretlemesiyle); olmayan yazı `body_html`'iyle basılıyor. */
  const postScope = postChrome(opts.lang);
  const content: SiteContent = {
    ...source,
    posts: source.posts.map((p) =>
      p.blocks ? { ...p, bodyHtml: { tr: renderBlocks(p.blocks, { ...renderCtx(postScope), prose: true }) } } : p
    )
  };

  /** Menü, telefon menüsü ve footer — sayfanın derinliğine göre bağlantılarıyla. */
  const chromeParts = (scope: Record<string, unknown>) => {
    const c = renderCtx(scope);
    return {
      siteHeader: nav && !nav.style?.hidden ? renderComponent(nav, c) : '',
      siteMenu: nav && !nav.style?.hidden ? renderMobileMenu(nav, c) : '',
      siteFooter: footer && !footer.style?.hidden ? renderComponent(footer, c) : ''
    };
  };

  const fill = (name: string, scope: Record<string, unknown>) => {
    const source = t.pages[name];
    if (source === undefined) throw new Error(`Sayfa şablonu yok: ${name}`);
    return render(source, { ...scope, ...chromeParts(scope) }, t.partials);
  };

  const pageHead = (name: PageName, scope: Record<string, unknown>) => renderBlocks(doc.pages[name], renderCtx(scope));

  function page(key: PageKey, slug?: string): string {
    switch (key) {
      case 'index': {
        const scope = {
          ...homeChrome(opts.lang),
          turnstile: opts.turnstile,
          featured: coverflowView(content, ctx),
          services: servicesView(content.services, opts.lang)
        };
        return fill('index', { ...scope, sections: renderBlocks(doc.home, renderCtx(scope)) });
      }
      case 'works': {
        const scope = { ...innerChrome(opts.lang), works: worksView(content, ctx) };
        return fill('works', { ...scope, pageHead: pageHead('works', scope) });
      }
      case 'album': {
        const album = content.albums.find((a) => a.slug === slug) ?? content.albums[0];
        if (!album) return '';
        return fill('album', { ...innerChrome(opts.lang), ...albumView(album, ctx) });
      }
      case 'blog': {
        const scope = { ...innerChrome(opts.lang), posts: postCardsView(content.posts, opts.lang) };
        return fill('blog', { ...scope, pageHead: pageHead('blog', scope) });
      }
      case 'post': {
        const i = Math.max(0, content.posts.findIndex((p) => p.slug === slug));
        if (!content.posts[i]) return '';
        return fill('post', { ...postChrome(opts.lang), ...postPageView(content.posts, i, opts.lang) });
      }
      case '404': {
        const scope = notFoundChrome(opts.lang);
        return fill('404', { ...scope, pageHead: pageHead('notFound', scope) });
      }
    }
  }

  return { page, en, doc, content };
}
